import './RateTableEditableCell.less';

import { GlobalOutlined } from '@ant-design/icons';
import Icon from '@ant-design/icons';
import { Button, Form, InputNumber, Select, Tooltip } from 'antd';
import { values } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';

import { getInAddressTerminals } from '../../library/util';
import AddressSearchInput from '../common/AddressSearchInput';
import AddBusinessSvg from '../icons/AddBusinessSvg';
import OrgDestTerminalAssignModal from '../modals/OrgDestTerminalAssignModal';
import { RateTableContext } from './RateTable';

/*
export interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string | string[];
  record?: Rate;
  rowIndex: number;
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}
*/

const TableCell = styled.td`
  &.table-cell-editable {
    padding: 5px 1px !important;
  }
`;

const RateTableEditableCell = ({
  align,
  children,
  dataIndex,
  editing,
  record,
  rowIndex,
  ...restProps
}) => {
  const {
    carrierClassesMap,
    carrierModesMap,
    carrierOptionMap,
    carriers,
    changedRowCarrierId,
    form: tableForm,
    isShowingExtraColumns,
    onShowDestinationMap,
    onShowOriginMap,
    shipTypes,
  } = useContext(RateTableContext);

  const [isTerminalAssignOpened, setIsTerminalAssignOpened] = useState(null);
  const [formValues, setFormValues] = useState({});

  const rowCarrierId = useMemo(() => {
    if (changedRowCarrierId && editing) {
      return changedRowCarrierId;
    }
    return record ? record.carrierId : undefined;
  }, [changedRowCarrierId, editing, record]);

  const currentCarrier = useMemo(() => {
    return carriers[rowCarrierId || 0];
  }, [carriers, rowCarrierId]);

  const isClassColumn = useMemo(
    () => Array.isArray(dataIndex) && dataIndex[0] === 'classRate',
    [dataIndex]
  );

  const modes = useMemo(() => {
    if (!rowCarrierId) return [];
    if (
      carrierModesMap &&
      carrierModesMap[rowCarrierId] &&
      carrierModesMap[rowCarrierId].modes
    ) {
      return (
        carrierModesMap[rowCarrierId].modes.filter((mode) => mode.active) || []
      );
    } else {
      return [];
    }
  }, [carrierModesMap, rowCarrierId]);

  const classes = useMemo(() => {
    if (!rowCarrierId || !carrierClassesMap || !carrierClassesMap[rowCarrierId])
      return [];
    return carrierClassesMap[rowCarrierId].classes || [];
  }, [carrierClassesMap, rowCarrierId]);

  const option = useMemo(() => {
    if (!rowCarrierId || !carrierClassesMap || !carrierClassesMap[rowCarrierId])
      return undefined;
    return carrierOptionMap[rowCarrierId].option;
  }, [carrierClassesMap, carrierOptionMap, rowCarrierId]);

  useEffect(() => {
    if (record && editing) {
      tableForm.setFieldsValue(record);
      setFormValues(record);
    }
  }, [record, editing, tableForm]);

  useEffect(() => {
    const handleValuesChange = (changedValues, allValues) => {
      setFormValues(allValues);
    };
    tableForm.onValuesChange = handleValuesChange;
    return () => {
      tableForm.onValuesChange = undefined;
    };
  }, [tableForm]);

  const filterOption = useCallback((input, option) => {
    return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
  }, []);

  const staticNode = useMemo(() => {
    if (dataIndex === 'carrierId') {
      return carriers[record.carrierId || 0].name || '';
    }
    if (dataIndex === 'shipTypeId' || dataIndex === 'destinationShipTypeId') {
      if (!record) {
        return 'No Record';
      }

      const shipTypeIdValue = record[dataIndex];
      if (!shipTypeIdValue) {
        return 'No Ship Type';
      }

      const shipTypeObj = shipTypes.find(
        (shipType) => shipType.id === shipTypeIdValue
      );

      if (!shipTypeObj) {
        return `Unknown Ship Type (ID: ${shipTypeIdValue})`;
      }

      const shipTypeName = shipTypeObj.name;
      if (shipTypeName !== 'Terminal') {
        return shipTypeName;
      }
      let rateTerminals = null;
      if (record) {
        if (dataIndex === 'shipTypeId') {
          rateTerminals = record.originTerminals;
        } else {
          rateTerminals = record.destinationTerminals;
        }
      }

      let terminalsTooltip = '';
      if (rateTerminals) {
        terminalsTooltip = rateTerminals
          .map((t) => t && t.companyName)
          .filter(Boolean)
          .join(', ');
      }
      return (
        <Tooltip title={terminalsTooltip}>
          {shipTypeName}
          {terminalsTooltip && <div className="terminal-indicator" />}
        </Tooltip>
      );
    }
    if (dataIndex === 'modeId') {
      const recordMode = modes.find((mode) => mode.id === record.modeId);
      return recordMode ? recordMode.name : (record.modeName ?? '');
    }
    if (dataIndex === 'origin') {
      return (
        <div
          style={{
            alignItems: 'flex-start',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          {record.origin}
          <Button
            icon={<GlobalOutlined />}
            size="small"
            style={{ color: 'green', marginRight: '5px' }}
            type="link"
            onClick={() => onShowOriginMap(record)}
          />
        </div>
      );
    }
    if (dataIndex === 'destination') {
      return (
        <div
          style={{
            alignItems: 'flex-start',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          {record.destination}
          <Button
            icon={<GlobalOutlined />}
            size="small"
            style={{ color: 'green', marginRight: '5px' }}
            type="link"
            onClick={() => onShowDestinationMap(record)}
          />
        </div>
      );
    }

    if (isClassColumn) {
      let result = '';
      // this used to use the second number as an index to look up the classRates and get the proper key
      // instead we can just get the key directly
      const classIndex = Number(dataIndex[1]);

      if (classIndex >= classes.length) {
        return 'N/A';
      }
      if (!record) {
        return '';
      }
      const classification = classes[classIndex];

      let matchingClassRate = record.classRate[classification.id];
      // get the value from ClassRate object (which has { value: number, updatedAt?: Date })
      const rateValue = matchingClassRate?.value;

      let lastUpdated = '';
      if (isShowingExtraColumns && matchingClassRate?.updatedAt) {
        lastUpdated = new Date(matchingClassRate.updatedAt)
          .toLocaleDateString('en-US')
          .replaceAll('/', '-');
      }

      // check if matchingClassRate.updatedAt is more than a year ago
      let isOld = false;
      if (matchingClassRate?.updatedAt) {
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        if (new Date(matchingClassRate.updatedAt) < oneYearAgo) {
          isOld = true;
        }
      }

      let opacity = isOld ? 0.5 : 1.0;

      if (
        option &&
        option.fuelSurcharge &&
        rateValue &&
        !record.skipFuelSurcharge
      ) {
        const surchargeAppliedRate =
          rateValue + (rateValue * option.fuelSurcharge) / 100;

        const surchargeAppliedValue = surchargeAppliedRate.toFixed(2);
        let costPerKm = 0;
        if (record.distance && record.distance > 0) {
          costPerKm = surchargeAppliedRate / record.distance;
        }

        const toolTipContents = `$${rateValue.toFixed(2)} (${costPerKm.toFixed(
          3
        )}$/km)`;

        result = (
          <Tooltip title={toolTipContents}>
            <div style={{ opacity: opacity }}>${surchargeAppliedValue}</div>
            <div
              style={{ fontSize: '90%', fontStyle: 'italic', opacity: opacity }}
            >
              {lastUpdated}
            </div>
          </Tooltip>
        );
      } else if (rateValue) {
        // this sort of duplicates the above but it's messy enough to not be worth refactoring to save 4 lines
        let costPerKm = 0;
        if (record.distance && record.distance > 0) {
          costPerKm = rateValue / record.distance;
        }
        const toolTipContents = `${costPerKm.toFixed(3)}$/km`;

        result = (
          <Tooltip title={toolTipContents}>
            <div style={{ opacity: opacity }}>${rateValue.toFixed(2)}</div>
            <div
              style={{ fontSize: '90%', fontStyle: 'italic', opacity: opacity }}
            >
              {lastUpdated}
            </div>
          </Tooltip>
        );
      }
      return result || '';
    }
    return children;
  }, [
    dataIndex,
    isClassColumn,
    children,
    carriers,
    record,
    shipTypes,
    modes,
    onShowOriginMap,
    onShowDestinationMap,
    classes,
    isShowingExtraColumns,
    option,
  ]);

  const handleTerminalsAssign = useCallback(
    (terminals) => {
      tableForm.setFields([
        {
          name:
            isTerminalAssignOpened === 'origin'
              ? 'originTerminals'
              : 'destinationTerminals',
          value: terminals,
        },
      ]);
      setIsTerminalAssignOpened(null);
    },
    [isTerminalAssignOpened, tableForm]
  );

  const availableTerminals = useMemo(() => {
    if (!isTerminalAssignOpened) {
      return [];
    }
    return getInAddressTerminals(
      currentCarrier?.terminals,
      tableForm.getFieldValue(isTerminalAssignOpened)
    );
  }, [currentCarrier?.terminals, tableForm, isTerminalAssignOpened]);

  const onTerminalsAssign = useCallback(
    (type) => {
      const origin = tableForm.getFieldValue('origin');
      const destination = tableForm.getFieldValue('destination');

      if (
        !currentCarrier ||
        (type === 'origin' && !origin) ||
        (type === 'destination' && !destination)
      ) {
        return;
      }

      setIsTerminalAssignOpened(type);
    },
    [currentCarrier, tableForm]
  );

  // editable field types
  const inputNode = useMemo(() => {
    if (dataIndex === 'carrierId') {
      return (
        <Select>
          {values(carriers).map((carrier) => (
            <Select.Option key={carrier.id} value={carrier.id}>
              {carrier.name}
            </Select.Option>
          ))}
        </Select>
      );
    }
    if (dataIndex === 'shipTypeId' || dataIndex === 'destinationShipTypeId') {
      const address =
        dataIndex === 'shipTypeId' ? formValues.origin : formValues.destination;
      const currentTerminals = getInAddressTerminals(
        currentCarrier?.terminals,
        address
      );

      return (
        <Select showSearch filterOption={filterOption}>
          {shipTypes.map((shipType) => (
            <Select.Option
              key={shipType.id}
              label={shipType.name}
              value={shipType.id}
            >
              <div
                style={{
                  alignItems: 'center',
                  display: 'inline-flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                {shipType.name}
                {shipType.name === 'Terminal' &&
                  window.location.pathname.includes('/carrier/') &&
                  currentTerminals.length > 0 && (
                    <Button
                      icon={<Icon component={AddBusinessSvg} />}
                      type="link"
                      onClick={() => {
                        onTerminalsAssign(
                          dataIndex === 'shipTypeId' ? 'origin' : 'destination'
                        );
                      }}
                    />
                  )}
              </div>
            </Select.Option>
          ))}
        </Select>
      );
    }
    if (dataIndex === 'modeId') {
      return (
        <Select showSearch filterOption={filterOption}>
          {modes.map(
            (mode) =>
              mode.id !== null && (
                <Select.Option key={mode.id} label={mode.name} value={mode.id}>
                  {mode.name}
                </Select.Option>
              )
          )}
        </Select>
      );
    }
    if (dataIndex === 'origin' || dataIndex === 'destination') {
      return <AddressSearchInput />;
    }
    if (dataIndex === 'transitDays') {
      return <InputNumber />;
    }
    if (isClassColumn) {
      const classIndex = Number(dataIndex[1]);
      if (classIndex >= classes.length) {
        return 'N/A';
      }
      return (
        <InputNumber
          controls={false}
          style={{ color: 'blue', textAlign: 'center' }}
          type="number"
        />
      );
    }
    return children;
  }, [
    carriers,
    children,
    classes.length,
    currentCarrier?.terminals,
    dataIndex,
    filterOption,
    formValues,
    isClassColumn,
    modes,
    onTerminalsAssign,
    shipTypes,
  ]);

  const tableCellStyle = useMemo(() => {
    const style = {
      textAlign: align,
    };
    if (editing) {
      style.padding = '5px 1px';
    } else {
      style.padding = '10px 13px';
    }
    if (isClassColumn) {
      style.width = 120;
      style.textAlign = 'center';
    }
    return style;
  }, [align, editing, isClassColumn]);

  const formItemName = useMemo(() => {
    if (isClassColumn) {
      const classIndex = Number(dataIndex[1]);

      if (classIndex >= classes.length) {
        return undefined;
      }

      return [dataIndex[0], classes[classIndex].id, 'value'];
    }
    return dataIndex;
  }, [classes, dataIndex, isClassColumn]);

  return (
    <TableCell {...restProps} style={tableCellStyle}>
      {editing ? (
        <Form.Item name={formItemName} style={{ margin: 0 }}>
          {inputNode}
        </Form.Item>
      ) : (
        staticNode
      )}
      {isTerminalAssignOpened && availableTerminals.length > 0 && (
        <OrgDestTerminalAssignModal
          initialTerminals={
            isTerminalAssignOpened === 'origin'
              ? tableForm.getFieldValue('originTerminals') || []
              : tableForm.getFieldValue('destinationTerminals') || []
          }
          terminals={availableTerminals}
          type={isTerminalAssignOpened}
          onClose={() => setIsTerminalAssignOpened(null)}
          onSubmit={handleTerminalsAssign}
        />
      )}
    </TableCell>
  );
};

export default RateTableEditableCell;
